import { useEffect, useState } from 'react';
import { Event } from '../types';
import { mockBackend } from '../services/mockBackend';
import EventCard from './EventCard';
import EventDescription from './EventDescription';
import EventDetailsGrid from './EventDetailsGrid';
import StatusBadge from './StatusBadge';

type CurationPanelProps = {
  adminId: string;
  refreshToken: number;
  onActionComplete: () => void;
};

const reviewFieldOptions = [
  'Título',
  'Descrição',
  'Data ou horário',
  'Local',
  'Instituição',
  'Promotor',
  'Formato',
  'Público',
  'Estimativa de público',
  'Acesso ou ingressos',
  'Link de inscrição',
  'Responsabilidade',
];

const CurationPanel = ({ adminId, refreshToken, onActionComplete }: CurationPanelProps) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [feedback, setFeedback] = useState('');
  const [selectedReviewFields, setSelectedReviewFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const items = await mockBackend.events.listCurationEvents();
      setEvents(items);
      setSelectedEvent((current) =>
        current ? items.find((item) => item.id === current.id) || null : current
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [refreshToken]);

  useEffect(() => {
    setFeedback('');
    setSelectedReviewFields([]);
  }, [selectedEvent?.id]);

  const withReload = async (callback: () => Promise<unknown>) => {
    await callback();
    await loadEvents();
    onActionComplete();
  };

  const toggleReviewField = (field: string) => {
    setSelectedReviewFields((current) =>
      current.includes(field) ? current.filter((item) => item !== field) : [...current, field]
    );
  };

  const buildReviewMessage = (fallback: string) => {
    const parts: string[] = [];
    if (selectedReviewFields.length > 0) {
      parts.push(`Campos que precisam de atenção: ${selectedReviewFields.join(', ')}.`);
    }
    if (feedback.trim()) {
      parts.push(feedback.trim());
    }

    return parts.join('\n\n') || fallback;
  };

  return (
    <div className="d-grid gap-4">
      <div className="card bg-onyx border-0 shadow-lg">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="text-white mb-1">Fila de curadoria</h5>
              <p className="text-white-50 mb-0">
                Eventos enviados, consolidados por match ou em reanálise.
              </p>
            </div>
            <span className="badge bg-brand text-white">{events.length}</span>
          </div>

          {loading ? (
            <div className="text-white-50">Carregando fila...</div>
          ) : (
            <div className="row g-3">
              {events.map((event) => (
                <div className="col-xl-6" key={event.id}>
                  <EventCard event={event} onClick={setSelectedEvent} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedEvent && (
        <div className="event-modal-backdrop" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal submission-modal card bg-onyx border-0 shadow-lg" onClick={(event) => event.stopPropagation()}>
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StatusBadge kind="event" status={selectedEvent.status} />
                    {selectedEvent.sourceEventIds.length > 0 && (
                      <span className="badge bg-dark text-white border border-secondary">
                        Match convertido
                      </span>
                    )}
                  </div>
                  <h4 className="text-white mb-1">{selectedEvent.title}</h4>
                  <p className="text-white-50 mb-0">{selectedEvent.institution}</p>
                </div>
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setSelectedEvent(null)}>
                  Fechar
                </button>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-3">Dados preenchidos</div>
                  <EventDetailsGrid event={selectedEvent} showStatus showResponsibility />
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-2">Descrição enviada</div>
                  <EventDescription
                    description={selectedEvent.description}
                    format={selectedEvent.descriptionFormat}
                  />
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-3">Histórico</div>
                  <div className="timeline-list">
                    {selectedEvent.history.map((item) => (
                      <div key={item.id} className="timeline-item">
                        <strong className="text-white d-block">{item.message}</strong>
                        <small className="text-white-50">
                          {new Date(item.createdAt).toLocaleString('pt-BR')}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="text-white fw-semibold mb-2">Campos para revisar</div>
                <div className="review-field-grid mb-3">
                  {reviewFieldOptions.map((field) => (
                    <label className="review-field-option" key={field}>
                      <input
                        className="form-check-input input-contrast"
                        type="checkbox"
                        checked={selectedReviewFields.includes(field)}
                        onChange={() => toggleReviewField(field)}
                      />
                      <span>{field}</span>
                    </label>
                  ))}
                </div>
                <label className="form-label text-white" htmlFor="adminFeedback">
                  Devolutiva administrativa
                </label>
                <textarea
                  id="adminFeedback"
                  className="form-control input-contrast"
                  rows={3}
                  value={feedback}
                  onChange={(event) => setFeedback(event.target.value)}
                  placeholder="Use ao pedir ajustes ou rejeitar."
                />
              </div>

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() =>
                    withReload(() =>
                      mockBackend.events.approveEvent(
                        selectedEvent.id,
                        adminId,
                        feedback || 'Evento aprovado e pronto para publicação.'
                      )
                    )
                  }
                >
                  Aprovar
                </button>
                <button
                  type="button"
                  className="btn btn-brand"
                  onClick={() =>
                    withReload(() =>
                      mockBackend.events.requestChanges(
                        selectedEvent.id,
                        adminId,
                        buildReviewMessage('Ajuste as informações apontadas antes de reenviar o evento.')
                      )
                    )
                  }
                >
                  Pedir ajustes
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={() =>
                    withReload(() =>
                      mockBackend.events.rejectEvent(
                        selectedEvent.id,
                        adminId,
                        buildReviewMessage('Evento rejeitado pela administração.')
                      )
                    )
                  }
                >
                  Rejeitar
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={() =>
                    withReload(() =>
                      mockBackend.events.cancelEvent(
                        selectedEvent.id,
                        adminId,
                        feedback || 'Evento cancelado pela administração.'
                      )
                    )
                  }
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurationPanel;
